export type IndexTone = "neutral" | "alert" | "busy";
export type IndexLayerId = "text" | "vectors" | "facts";
export type IndexLayerMark = "ok" | "fail";

export const INDEX_LAYER_ORDER: IndexLayerId[] = ["text", "vectors", "facts"];

export const INDEX_LAYER_LABELS: Record<IndexLayerId, string> = {
  text: "Текст",
  vectors: "Поиск",
  facts: "Wiki",
};

export const indexErrorCopy = {
  copyHint: "Нажмите, чтобы скопировать ошибку",
} as const;

export type DocumentIndexLayer = {
  id: IndexLayerId;
  tone: IndexTone;
  title: string;
};

const LAYER_COPY: Record<
  IndexLayerId,
  Record<IndexTone, string>
> = {
  text: {
    neutral: "Текст разобран",
    alert: "Ошибка разбора текста",
    busy: "Текст обрабатывается",
  },
  vectors: {
    neutral: "Индекс поиска готов",
    alert: "Векторы не записаны",
    busy: "Индекс поиска готовится",
  },
  facts: {
    neutral: "Факты Wiki извлечены",
    alert: "Факты Wiki не извлечены",
    busy: "Факты Wiki извлекаются",
  },
};

function layer(id: IndexLayerId, tone: IndexTone): DocumentIndexLayer {
  return { id, tone, title: LAYER_COPY[id][tone] };
}

function textTone(input: {
  parseStatus: string;
  indexStage?: string | null;
}): IndexTone {
  if (input.parseStatus === "FAILED") {
    return "alert";
  }
  if (
    input.parseStatus === "DONE" ||
    input.indexStage === "EMBED" ||
    input.indexStage === "EXTRACT"
  ) {
    return "neutral";
  }
  return "busy";
}

function vectorsTone(input: {
  parseStatus: string;
  vectorIndexedAt: Date | string | null;
  indexStage?: string | null;
}): IndexTone {
  if (input.parseStatus === "FAILED") {
    return "alert";
  }
  if (input.vectorIndexedAt) {
    return "neutral";
  }
  if (input.parseStatus !== "DONE" || input.indexStage === "EMBED") {
    return "busy";
  }
  return "alert";
}

function factsTone(input: {
  parseStatus: string;
  extractStatus: string;
  indexStage?: string | null;
}): IndexTone {
  if (input.parseStatus === "FAILED") {
    return "alert";
  }
  if (input.extractStatus === "DONE") {
    return "neutral";
  }
  if (input.extractStatus === "FAILED") {
    return "alert";
  }
  if (
    input.extractStatus === "PROCESSING" ||
    input.indexStage === "EXTRACT"
  ) {
    return "busy";
  }
  return "alert";
}

export function documentIndexLayers(input: {
  parseStatus: string;
  vectorIndexedAt: Date | string | null;
  extractStatus: string;
  extractError?: string | null;
  indexStage?: string | null;
}): DocumentIndexLayer[] {
  const facts = layer("facts", factsTone(input));
  if (
    facts.tone === "alert" &&
    input.parseStatus !== "FAILED" &&
    input.extractError?.trim()
  ) {
    facts.title = input.extractError.trim();
  }

  return [
    layer("text", textTone(input)),
    layer("vectors", vectorsTone(input)),
    facts,
  ];
}

export function indexLayerMark(tone: IndexTone): IndexLayerMark {
  return tone === "neutral" ? "ok" : "fail";
}
